// ============================================================
// useTracking.js — Hook de Tracking (Simulation Google Analytics)
// Utilise Intersection Observer pour détecter les sections vues
// et log chaque événement en console (remplacer par gtag() en prod)
// ============================================================

import { useEffect, useRef, useCallback } from 'react';
import ReactGA from 'react-ga4';

export function useTracking() {
  const sessionId = useRef(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const viewedSections = useRef(new Set());
  const gaInitialized = useRef(false);

  // Initialise ReactGA si une variable d'environnement est fournie (une seule fois)
  const initGA = useCallback(() => {
    if (gaInitialized.current) return;

    const measurementId =
      process.env.REACT_APP_GA_ID ||
      process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID ||
      process.env.VITE_GA_ID; // essayer plusieurs conventions d'env

    if (measurementId) {
      try {
        ReactGA.initialize(measurementId);
        gaInitialized.current = true;
      } catch (err) {
        console.warn('ReactGA initialization failed:', err);
      }
    } else {
      console.info('Aucun GA measurement ID fourni — tracking react-ga4 non initialisé.');
    }
  }, []);

  // Log formaté (simule l'envoi à GA4)
  const sendToAnalytics = useCallback((eventName, payload) => {
    const timestamp = new Date().toISOString();
    console.group(`%c📊 [PetAero Analytics] ${eventName}`, 'color: #C9A96E; font-weight: bold;');
    console.log('Timestamp:', timestamp);
    console.log('Session:', sessionId.current);
    console.log('Payload:', payload);
    console.groupEnd();

    // Initialiser si nécessaire puis envoyer via react-ga4 (gtag wrapper)
    initGA();

    try {
      if (gaInitialized.current && typeof ReactGA.gtag === 'function') {
        ReactGA.gtag('event', eventName, { ...payload, session_id: sessionId.current });
      } else if (typeof window.gtag === 'function') {
        // fallback si react-ga4 non initialisé mais gtag présent globalement
        window.gtag('event', eventName, { ...payload, session_id: sessionId.current });
      } else {
        // aucun outil de tracking disponible (mode dev)
        console.debug('No gtag available to send event:', eventName);
      }
    } catch (err) {
      console.warn('Erreur lors de l\'envoi de l\'événement analytics:', err);
    }
  }, [initGA]);

  // Fonction principale d'envoi d'événement (exposée aux composants)
  const trackEvent = useCallback((eventName, payload = {}) => {
    sendToAnalytics(eventName, payload);
  }, [sendToAnalytics]);

  // Intersection Observer : tracking automatique des sections vues
  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewedSections.current.has(entry.target.id)) {
            // Marquer la section comme vue (évite les doublons)
            viewedSections.current.add(entry.target.id);

            // Envoyer l'événement "section_view"
            sendToAnalytics('section_view', {
              section_id: entry.target.id,
              section_name: entry.target.dataset.trackingName || entry.target.id,
              visibility_ratio: Math.round(entry.intersectionRatio * 100) + '%',
            });
          }
        });
      },
      {
        threshold: 0.3, // Déclenché quand 30% de la section est visible
        rootMargin: '-50px 0px',
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [sendToAnalytics]);

  return { trackEvent };
}